import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.split";
import "core-js/modules/web.dom-collections.iterator";
import "core-js/modules/es.string.includes";
import "core-js/modules/es.string.split";
import "core-js/modules/web.dom-collections.iterator";
/**
 * Component: Cart table
 * ------------------------------------------------------------------------------
 * An event based Ajax cart table.
 *
 * @namespace cartTable
 */

import * as cart from '@shopify/theme-cart';
import { formatMoney } from '@shopify/theme-currency';
import { getSizedImageUrl } from '@shopify/theme-images';
import axios from 'axios';
import { misc as icons } from '~/helpers/svg-map';
import cssClasses from '~/helpers/cssClasses';
import { on, extendDefaults } from '~/helpers/utils';
import quantitySelectorTemplate from '~/components/templates/quantity-selector';
import QuantitySelector from '~/components/quantity-selector';
/**
 * DOM selectors.
 */

const selectors = {
  ajaxCart: 'js-ajax-cart',
  lineItem: '[js-cart-table="lineItem"]',
  lineItemPrice: '[js-cart-table="lineItemPrice"]',
  quantitySelector: '[js-cart-table="quantitySelector"]',
  subtotal: '[js-cart-table="subtotal"]',
  total: '[js-cart-table="total"]',
  totalDiscountContainer: '[js-cart-table="totalDiscountContainer"]',
  totalDiscountPrice: '[js-cart-table="totalDiscountPrice"]'
};
/**
 * Create new cart table object.
 * @param {String} selector - DOM selector.
 * @param {Object} config - Configuration.
 */

export default ((selector, config) => {
  /**
   * Instance default settings.
   */
  const defaults = {
    hideAddOns: true
  };
  /**
   * DOM node selectors.
   */

  const nodeSelectors = {
    container: document.querySelector(selector),
    subtotal: document.querySelectorAll(selectors.subtotal),
    total: document.querySelector(selectors.total),
    totalDiscountContainer: document.querySelector(selectors.totalDiscountContainer),
    totalDiscountPrice: document.querySelector(selectors.totalDiscountPrice)
  };
  /**
   * Instance settings.
   */

  const settings = extendDefaults(defaults, config);
  /**
   * Instance globals.
   */

  let timer = {};
  /**
   * Initialise component.
   */

  function init() {
    requestCart();
    setEventBusListeners();
    setEventListeners(nodeSelectors.container);
  }
  /**
   * Set EventBus listeners.
   */


  function setEventBusListeners() {
    Frame.EventBus.listen('AjaxCart:updated', response => render(response));
    Frame.EventBus.listen('AjaxCart:removingItem', item => setLoadingState(item));
  }
  /**
   * Requests cart.js data for rendering.
   */


  function requestCart() {
    Frame.EventBus.emit('CartTable:updating');
    axios.get("/cart.js?no-cache=".concat(new Date().getTime())).then(response => {
      render(response.data);
    }).catch(error => {
      throw new Error(error);
    });
  }
  /**
   * Render cart template.
   * @param {Object} data - Cart.js data.
   */


  function render(data) {
    nodeSelectors.container.innerHTML = getCartTemplate(data);
    constructQuantitySelectors();
    updateCartPrices(data);
    Frame.EventBus.emit('CartTable:rendered', data);
  }
  /**
  * Get cart template
  * @param {Object} data - Cart data.
  * @returns {String}
  */


  function getCartTemplate(data) {
    const lineItemsTemplate = data.items.map((item, index) => {
      if (isAddOnItem(item)) {
        return '';
      }

      return getLineItemTemplate(item, index);
    }).join('');
    return "\n      <div class=\"cart-table__body ajax-cart\">\n        ".concat(lineItemsTemplate, "\n      </div>\n    ");
  }
  /**
   * Get line item template.
   * @param {Object} item - Line item data object.
   * @returns {String}
   */


  function getLineItemTemplate(item, index) {
    const itemTitleRaw = item.product_title.split('|');
    let itemTitle = item.product_title;

    if (itemTitleRaw.length > 0) {
      itemTitle = itemTitleRaw[0];
    }

    return "\n      <div\n        class=\"cart-table__item ajax-cart__line-item\"\n        data-key=\"".concat(item.key, "\"\n        data-index=\"").concat(index, "\"\n        js-cart-table=\"lineItem\"\n      >\n        <div class=\"cart-table__image ajax-cart__image\">\n          <a href=\"").concat(item.url, "\">\n            ").concat(getLineItemImage(item), "\n          </a>\n        </div>\n\n        <div class=\"cart-table__description\">\n          <div class=\"ajax-cart__inner\">\n            ").concat(isVendorEnabled() ? getVendorTitle(item) : '', "\n\n            <a class=\"ajax-cart__title h6\" href=\"").concat(item.url, "\">\n              ").concat(itemTitle, "\n            </a>\n\n            ").concat(getVariantTitle(item), "\n          </div>\n\n          <div class=\"ajax-cart__amount\">\n            ").concat(quantitySelectorTemplate(item, index, 'cart-table'), "\n          </div>\n\n          <div class=\"ajax-cart__price h6\" js-cart-table=\"lineItemPrice\">\n            ").concat(getLineItemPrice(item), "\n          </div>\n\n          <button\n            class=\"ajax-cart__remove\"\n            data-cy=\"removeProduct\"\n            data-key=\"").concat(item.key, "\"\n            js-ajax-cart=\"remove\"\n          >\n            ").concat(icons.close, "\n            <span class=\"visually-hidden\">\n              ").concat(theme.strings.cart.general.remove, "\n            </span>\n          </button>\n        </div>\n      </div>\n\n      ").concat(getPreorderMessage(item), "\n    ");
  }
  /**
   * Get line item image template.
   * @param {Object} item - Variant line item object from cart.js.
   * @returns {String}
   */


  function getLineItemImage(item) {
    if (!item.image) {
      return '';
    }

    return "\n    <div class=\"ajax-cart__image responsive-image__wrapper\">\n      <img\n        alt=\"".concat(item.title, "\"\n        sizes=\"auto\"\n        src=\"").concat(getSizedImageUrl(item.image, '50x'), "\"\n        srcset=\"\n          ").concat(getSizedImageUrl(item.image, '120x'), " 120w,\n          ").concat(getSizedImageUrl(item.image, '240x'), " 240w,\n          ").concat(getSizedImageUrl(item.image, '320x'), " 320w\n        \"\n      >\n    </div>\n    ");
  }
  /**
   * Check if current item is an add-on.
   * @param {Object} item - Cart variant item.
   * @returns {Boolean}
   */


  function isAddOnItem(item) {
    if (!item) {
      throw Error('Could not find item');
    }

    return settings.hideAddOns && item.product_type.toLowerCase() === 'add-on';
  }
  /**
   * Check if vendor title setting is enabled.
   * @returns {Boolean}
   */


  function isVendorEnabled() {
    return JSON.parse(nodeSelectors.container.dataset.enableVendorTitle);
  }
  /**
   * Get vendor title template.
   * @param {Object} item - Cart item to get vendor title from.
   * @returns {String}
   */


  function getVendorTitle(item) {
    return "<p class=\"ajax-cart__vendor meta\">".concat(item.vendor, "</p>");
  }
  /**
   * Get variant titles.
   * @param {Object} item - Line item data object.
   * @returns {String}
   */


  function getVariantTitle(item) {
    return item.variant_options.length && !item.variant_options[0].includes('Default Title') ? "<p class=\"ajax-cart__variant-title body-2\">".concat(item.variant_title, "</p>") : '';
  }
  /**
   * Get Preorder message if item has preorder property.
   * @param {Object} item - Cart line item object data.
   * @returns {string} - Template.
   */


  function getPreorderMessage(item) {
    const isPreorder = ("_preorder" in item.properties);

    if (!isPreorder) {
      return '';
    }

    return "\n      <div class=\"preorder-notification preorder-notification--cart\" role=\"alert\">\n        <p js-preorder-notification=\"text\">\n          ".concat(theme.strings.preorder_message, "\n        </p>\n      </div>");
  }
  /**
   * Get formatted line item price.
   * @param {Object} item - Line item data object.
   * @returns {String}
   */


  function getLineItemPrice(item) {
    var _item$properties;

    let lineItemTemplate = "";
    const compareAtPrice = ((_item$properties = item.properties) === null || _item$properties === void 0 ? void 0 : _item$properties.compare_at_price) || 0;

    if (compareAtPrice > item.price) {
      lineItemTemplate += "\n        <span\n          class=\"body-2 ajax-cart__price--compare\"\n          data-compare-at-price=\"".concat(compareAtPrice, "\"\n          js-currency=\"compare_at_price\">\n          ").concat(formatMoney(compareAtPrice, theme.moneyFormat), "\n        </span>\n      ");
    }

    if (item.original_line_price !== item.line_price) {
      if (item.discounted_price !== item.price) {
        lineItemTemplate += "\n          <span class=\"visually-hidden\">".concat(item.discounted_price, "</span>\n          <span js-currency=\"price\" data-price=\"").concat(item.discounted_price, "\">\n            ").concat(formatMoney(item.discounted_price, theme.moneyFormat), "\n          </span>\n\n          <span class=\"visually-hidden\">").concat(item.price, "</span>\n          <s js-currency=\"price\" data-price=\"").concat(item.price, "\">\n            ").concat(formatMoney(item.price, theme.moneyFormat), "\n          </s>\n        ");
      }

      lineItemTemplate += "\n        <span class=\"visually-hidden\">".concat(item.price, "</span>\n        <span js-currency=\"price\" data-price=\"").concat(item.price, "\">\n          ").concat(formatMoney(item.price, theme.moneyFormat), "\n        </span>\n\n        <span class=\"visually-hidden\">").concat(item.original_price, "</span>\n        <s js-currency=\"price\" data-price=\"").concat(item.original_price, "\">\n          ").concat(formatMoney(item.original_price, theme.moneyFormat), "\n        </s>\n      ");
    }

    lineItemTemplate += "\n      <span class=\"\n        body-2\n        ".concat(compareAtPrice > item.price ? 'ajax-cart__price--sale subtitle-2' : '', "\"\n        data-price=\"").concat(item.price, "\"\n        js-currency=\"price\"\n      >\n        ").concat(formatMoney(item.price, theme.moneyFormat), "\n      </span>\n      <div data-product-title=\"{{item.price}}\"></div>\n    ");
    return lineItemTemplate;
  }
  /**
   * Set event delegation to remove items on container.
   * @param {HTMLElement} container - The container to apply the remove events on.
   */


  function setEventListeners(container) {
    on('click', container, event => handleRemoveEvent(event));
  }
  /**
   * Handle remove events.
   * @param {Event} event - Click event.
   */


  function handleRemoveEvent(event) {
    if (!isTargetRemove(event.target)) {
      return;
    }

    event.preventDefault();
    Frame.EventBus.emit('AjaxCart:removingItem', event.target.closest(selectors.lineItem));
    cart.removeItem(event.target.dataset.key).then(response => {
      Frame.EventBus.emit('AjaxCart:itemRemoved', response);
      return response;
    }).catch(error => {
      window.location.reload();
      throw new Error('Could not remove item from cart', error);
    });
  }
  /**
   * Detect remove target click.
   * @param {HTMLElement} target - Clicked target to check remove attributes against.
   * @returns {Boolean}
   */


  function isTargetRemove(target) {
    return typeof target.attributes[selectors.ajaxCart] !== 'undefined' && target.attributes[selectors.ajaxCart].nodeValue === 'remove';
  }
  /**
   * Instantiate quantity selectors using id.
   * - Callback has a timer to reduce number of calls.
   */


  function constructQuantitySelectors() {
    const quantitySelectors = document.querySelectorAll(selectors.quantitySelector);
    [...quantitySelectors].forEach(item => {
      QuantitySelector("".concat(item.id), () => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
          handleQuantityEvent(item);
        }, 250);
      }).setInitState();
    });
  }
  /**
   * Handle quantity updates.
   * @param {HTMLElement} element - Quantity selector node element.
   */


  function handleQuantityEvent(element) {
    Frame.EventBus.emit('CartTable:updating');
    const data = {
      index: element.closest(selectors.lineItem).dataset.index,
      key: element.closest(selectors.lineItem).dataset.key,
      quantity: parseInt(element.querySelector('input').value, 10)
    };
    cart.updateItem(data.key, {
      quantity: data.quantity
    }).then(() => {
      Frame.EventBus.emit('AjaxCart:itemUpdated');
    }).catch(error => {
      if (typeof response === 'undefined') {
        return;
      }

      throw new Error('Could not update item data', error);
    });
  }
  /**
   * Update subtotal and discount total.
   * @param {Object} data - Cart response data.
   */


  function updateCartPrices(data) {
    updateSubtotal(data.items_subtotal_price);
    updateDiscountTotal(data.total_discount);
    updateTotal(data.total_price);
  }
  /**
  * Update subtotal.
  * @param {Number} price - Total subtotal price number.
  */


  function updateSubtotal(price) {
    [...nodeSelectors.subtotal].forEach(element => {
      element.setAttribute('data-price', price);
      element.innerHTML = formatMoney(price, theme.moneyFormat);
    });
  }
  /**
  * Update total.
  * @param {Number} price - Total cart price number.
  */


  function updateTotal(price) {
    nodeSelectors.total.setAttribute('data-price', price);
    nodeSelectors.total.innerHTML = formatMoney(price, theme.moneyFormat);
  }
  /**
   * Update discount total.
   * @param {Number} price - Total discount price number.
   */


  function updateDiscountTotal(price) {
    if (!isCartDiscounted(price)) {
      hideDiscountLabel();
      return;
    }

    showDiscountLabel();
    nodeSelectors.totalDiscountPrice.setAttribute('data-price', price);
    nodeSelectors.totalDiscountPrice.innerHTML = formatMoney(price, theme.moneyFormat);
  }
  /**
   * Set loading state.
   * @param {Object} element - Line item row in DOM.
   */


  function setLoadingState(element) {
    Frame.EventBus.emit('CartTable:updating');
    element.classList.add(cssClasses.isRemoving);
  }
  /**
   * Check discount total.
   * @param {Number} total - Total discount price number.
   */


  function isCartDiscounted(total) {
    return total > 0;
  }
  /**
   * Hide discount label.
   */


  function hideDiscountLabel() {
    nodeSelectors.totalDiscountContainer.classList.add(cssClasses.hidden);
  }
  /**
   * Show discount label.
   */


  function showDiscountLabel() {
    nodeSelectors.totalDiscountContainer.classList.remove(cssClasses.hidden);
  }
  /**
   * Expose public interface.
   */


  return Object.freeze({
    init
  });
});